/**
 * What every part of the Desktop shares — the canvases, the icons and the
 * windows all read the same workspace, selection, drag and actions, provided
 * once by `DesktopView`.
 */
import { createContext, useContext } from 'react';
import type { VaultDocumentFolderDto } from '@/lib/api';
import type { DesktopLayoutController } from '../../hooks/useDesktopLayout';
import type { FileActionsController } from '../../hooks/useFileActions';
import type { Cell, FolderView } from '../../lib/desktop-layout';
import type { DesktopStyle } from '../../lib/folder-view';
import type { DesktopItem } from './desktop-model';
import type { DragSession, DropTarget } from './useCanvasDrag';

export interface DesktopSelection {
  container: string;
  keys: ReadonlySet<string>;
}

/**
 * What a canvas tells the Desktop about itself, for drops, paste, menus and
 * the status bars. Registered under the CANVAS — the window it's in, or the
 * container for the desktop itself — never just the container: a window can
 * show Documents while the desktop shows it too, and neither may clobber the
 * other's entry.
 */
export interface CanvasInfo {
  /** The container it shows. */
  container: string;
  positions: ReadonlyMap<string, Cell>;
  columns: number;
  items: readonly DesktopItem[];
  /** Files loaded so far, and how many the container holds. */
  loadedFiles: number;
  totalFiles: number;
  /** Icons sit where they're put (the desktop) — not a flowing window. */
  free: boolean;
}

export interface MenuRequest {
  x: number;
  y: number;
  container: string;
  /** The window the menu was opened in, if any. */
  windowId?: string;
  /** The item right-clicked; absent for the canvas background. */
  item?: DesktopItem;
  /** The cell under the pointer, for "New folder" there. */
  cell?: Cell;
  /** Where focus returns when the menu closes. */
  returnFocus?: HTMLElement | null;
}

export interface DesktopContextValue {
  holderId: string;
  /** Windows or Mac — chrome, keys, where the Trash lives. */
  style: DesktopStyle;
  /** How a container is viewed in a window (stored, else the default). */
  viewOf: (container: string) => FolderView;
  /** The desktop's own view: its icon size (and last "Sort by"). */
  desktopView: FolderView;
  setView: (container: string, view: FolderView) => void;
  folders: readonly VaultDocumentFolderDto[];
  foldersById: ReadonlyMap<string, VaultDocumentFolderDto>;
  layout: DesktopLayoutController;
  fileActions: FileActionsController;
  /** May this person change what's in folders (not just arrange)? */
  canWrite: boolean;
  /** The folder list and the saved arrangement have loaded. Until then a
   *  canvas arranges nothing: an early auto-placement would stick (loading
   *  more never reflows what's on screen). */
  ready: boolean;
  trashFull: boolean;

  selection: DesktopSelection;
  select: (container: string, keys: Iterable<string>, mode: 'replace' | 'toggle' | 'add') => void;
  /** Keys with a move or delete in flight — drawn as pending. */
  pending: ReadonlySet<string>;
  clipboard: DesktopSelection | null;

  drag: {
    session: DragSession | null;
    begin: (init: Omit<DragSession, 'target' | 'allowed' | 'reason'>) => void;
  };

  renaming: { container: string; key: string } | null;
  startRename: (container: string, key: string) => void;
  commitRename: (item: DesktopItem, name: string) => void;
  cancelRename: () => void;

  /** Open a file the preferred way, or a folder — in the window it's in
   *  (navigating it), or a new window when asked or when it's on the desktop. */
  openItem: (
    item: DesktopItem,
    container: string,
    options?: { newWindow?: boolean; windowId?: string },
  ) => void;
  /** Mac Space: a quick look at a file (the viewer). */
  quickLook: (item: DesktopItem) => void;
  openMenu: (request: MenuRequest) => void;
  /** Record where icons sit in a container (arrangement only). */
  arrange: (container: string, positions: ReadonlyArray<readonly [string, Cell]>) => void;
  /** Forget stored positions (they auto-arrange). */
  forget: (container: string, keys: readonly string[]) => void;
  /** `canvas`: the window id, or the container for the desktop itself. */
  registerCanvas: (canvas: string, info: CanvasInfo | null) => void;
  /** Bumped by "Tidy up" — a canvas drops its session placements when it changes. */
  tidyVersion: number;

  cut: (container: string, items: readonly DesktopItem[]) => void;
  paste: (container: string) => void;
  trash: (container: string, items: readonly DesktopItem[]) => void;
  uploadInto: (container: string, files: File[], cell: Cell | null) => void;
  /** The drop path, also used by keyboard paste. */
  performDrop: (
    session: Pick<DragSession, 'container' | 'items' | 'offsets'>,
    target: DropTarget,
  ) => void;
}

export const DesktopContext = createContext<DesktopContextValue | null>(null);

export function useDesktop(): DesktopContextValue {
  const value = useContext(DesktopContext);
  if (!value) throw new Error('useDesktop must be used inside DesktopView');
  return value;
}
